import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import { parseColor, rgbaWithAlpha } from './bioModalTheme';

interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  talentId?: string | null;
  talentFirstName?: string;
  onAuthenticated?: (user: { id: string; email: string; full_name?: string; avatar_url?: string | null; credits?: number }) => void;
  // Initial mode: 'auth' just wants to log in/register, 'full' shows a link to full account
  mode?: 'auth' | 'full';
  // Optional value-prop treatment — when supplied, the modal swaps its
  // generic "Sign in to support {first}" copy for a custom title/subtitle
  // and renders a perks checklist below the auth form. Used by the
  // bio page's exit-intent / 8-second "Become a Fan" popup so the auth
  // flow itself is reused but the framing is conversion-focused.
  valueProp?: {
    title: string;
    subtitle?: string;
    perks?: string[];
  };
  // Optional theme — when supplied, the modal picks up the talent's
  // brand color for accents (CTA button, perks checks, avatar ring,
  // success glow) and shows the talent's avatar in a glowing ring at
  // the top. Falls back to the generic ShoutOut treatment when omitted.
  theme?: {
    color?: string;     // primary accent, e.g. bioSettings.button_color
    avatarUrl?: string; // talent profile image
  };
}

const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

// Common consumer-domain typos. We auto-correct these on submit so a
// fan who types `gmail.co` (Colombia ccTLD that Gmail does NOT issue)
// or `gmial.com` doesn't lock themselves out of their own account
// forever. Mirrors public.normalize_email_typos() server-side.
const EMAIL_DOMAIN_FIXES: Record<string, string> = {
  'gmail.co': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmail.om': 'gmail.com',
  'yahoo.co': 'yahoo.com', 'yahoo.cm': 'yahoo.com',
  'hotmail.co': 'hotmail.com', 'hotmail.cm': 'hotmail.com',
  'outlook.co': 'outlook.com', 'outlook.cm': 'outlook.com',
  'aol.co': 'aol.com', 'aol.cm': 'aol.com',
  'icloud.co': 'icloud.com', 'icloud.cm': 'icloud.com',
  'comcast.ne': 'comcast.net', 'comcast.nt': 'comcast.net',
  'ymail.co': 'ymail.com', 'ymail.cm': 'ymail.com',
  'mail.co': 'mail.com', 'mail.cm': 'mail.com',
  'gmial.com': 'gmail.com', 'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com', 'gmali.com': 'gmail.com',
  'yhaoo.com': 'yahoo.com', 'yaho.com': 'yahoo.com',
  'hotnail.com': 'hotmail.com', 'hotmial.com': 'hotmail.com',
  'outloo.com': 'outlook.com', 'oultlook.com': 'outlook.com',
};

const correctEmailTypo = (email: string): string | null => {
  const parts = email.toLowerCase().trim().split('@');
  if (parts.length !== 2) return null;
  const fix = EMAIL_DOMAIN_FIXES[parts[1]];
  return fix ? `${parts[0]}@${fix}` : null;
};

const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, '').slice(0, 10);
  if (cleaned.length < 4) return cleaned;
  if (cleaned.length < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const AccountModal: React.FC<AccountModalProps> = ({
  open,
  onClose,
  talentId,
  talentFirstName,
  onAuthenticated,
  mode = 'auth',
  valueProp,
  theme,
}) => {
  const [step, setStep] = useState<'email' | 'phone' | 'otp' | 'done'>('email');
  const [email, setEmail] = useState('');
  // `phone` holds either the E.164 returned by the server for existing users, or
  // user-typed digits for new users. We always send this back to verify-registration-otp
  // so the server can locate the OTP row by phone (the fallback lookup path is brittle).
  const [phone, setPhone] = useState('');
  // isExistingUserFlow is true when the OTP was sent to the user's stored phone
  // (we showed a masked hint and skipped the phone-input step).
  const [isExistingUserFlow, setIsExistingUserFlow] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolvedUser, setResolvedUser] = useState<any>(null);
  const closeOnEscape = useRef(true);
  // Reviews shown as a horizontal scrolling carousel under the perks
  // checklist on the Become a Fan / value-prop variant. Loaded lazily
  // when the modal opens with both a talentId and a valueProp so we
  // don't hit the DB for the generic login flow.
  const [reviews, setReviews] = useState<Array<{
    id: string;
    rating: number;
    comment: string | null;
    full_name: string | null;
  }>>([]);
  const reviewScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('email');
    setOtp('');
    setPhone('');
    setIsExistingUserFlow(false);
    setResolvedUser(null);
    const saved = localStorage.getItem('bio_subscriber_email');
    if (saved) setEmail(saved);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape.current) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lazy-load reviews when the modal opens with a value-prop framing
  // (i.e. the Become a Fan exit-intent / 8-second popup). We pull the
  // five highest-rated reviews with a non-empty comment so the carousel
  // is always populated with social proof rather than terse 5-star
  // entries that have nothing to read.
  useEffect(() => {
    if (!open || !valueProp || !talentId) {
      setReviews([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('reviews')
          .select(`
            id,
            rating,
            comment,
            users!reviews_user_id_fkey ( full_name )
          `)
          .eq('talent_id', talentId)
          .gte('rating', 4)
          .not('comment', 'is', null)
          .order('rating', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(8);
        if (cancelled || !data) return;
        // Drop empty/whitespace-only comments and normalise the shape so
        // the renderer doesn't have to know about the foreign-key join.
        const cleaned = (data as any[])
          .filter((r) => (r.comment || '').trim().length > 0)
          .map((r) => ({
            id: r.id,
            rating: r.rating,
            comment: r.comment,
            full_name: r.users?.full_name || null,
          }));
        setReviews(cleaned);
      } catch (err) {
        console.warn('AccountModal reviews fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, valueProp, talentId]);

  // Active slide for the one-review-at-a-time carousel. Drives both
  // the auto-advance interval and the dot indicators below the
  // carousel so the user can see at a glance how many reviews are in
  // the rotation and which one they're on.
  const [reviewIndex, setReviewIndex] = useState(0);

  // Auto-advance every ~5s. Wrap-around at the end so the social
  // proof keeps cycling for as long as the popup is open. We also
  // imperatively scroll the carousel container so manual drags stay
  // in sync with the dots.
  useEffect(() => {
    if (!open || reviews.length <= 1) return;
    const id = window.setInterval(() => {
      setReviewIndex((i) => (i + 1) % reviews.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [open, reviews.length]);

  // Keep the scroll position pinned to the active slide whenever the
  // index changes (whether from the auto-advance or a manual dot tap).
  useEffect(() => {
    const el = reviewScrollRef.current;
    if (!el) return;
    const target = el.children[reviewIndex] as HTMLElement | undefined;
    if (!target) return;
    el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }, [reviewIndex]);

  // Reset to the first slide whenever the modal opens fresh, so a
  // returning visitor doesn't pick up where they left off.
  useEffect(() => {
    if (open) setReviewIndex(0);
  }, [open]);

  const linkFanToTalent = async (userId: string) => {
    if (!talentId || !userId) return;
    try {
      const { data: existing } = await supabase
        .from('talent_followers')
        .select('id, unsubscribed_at')
        .eq('user_id', userId)
        .eq('talent_id', talentId)
        .maybeSingle();
      if (!existing) {
        await supabase
          .from('talent_followers')
          .insert({ user_id: userId, talent_id: talentId });
      } else if (existing.unsubscribed_at) {
        await supabase
          .from('talent_followers')
          .update({ unsubscribed_at: null })
          .eq('id', existing.id);
      }
    } catch (err) {
      console.warn('linkFanToTalent failed', err);
    }
  };

  const submitEmail = async () => {
    if (!emailValid(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    // Auto-correct common typos before sending — gmail.co, gmial.com, etc.
    const corrected = correctEmailTypo(email);
    if (corrected && corrected !== email.toLowerCase().trim()) {
      setEmail(corrected);
      toast.success(`Auto-corrected to ${corrected}`);
    }
    const cleanEmail = (corrected || email).toLowerCase().trim();

    // Fire-and-forget: record the email as a bio lead the moment it
    // passes validation. If the visitor bails before finishing the
    // phone/OTP steps we still keep their email + which talent's page
    // they entered it on. The RPC is idempotent (unique on lower(email)
    // + talent_id + source) and silently skips emails already attached
    // to a registered user, so it's safe to call on every submit.
    try {
      void supabase.rpc('record_bio_email_lead', {
        p_email: cleanEmail,
        p_talent_id: talentId ?? null,
        p_source: valueProp ? 'become_fan_popup' : 'account_modal',
        p_referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      });
    } catch {}

    setLoading(true);

    // Capture the lead the moment we have a valid email — fire and forget.
    // If the user bails on the phone step we still keep their email + which
    // talent they were looking at so admin can re-engage. The RPC silently
    // skips emails that already belong to a registered user.
    try {
      const params = new URLSearchParams(window.location.search);
      void supabase.rpc('record_bio_email_lead', {
        p_email:        email.toLowerCase().trim(),
        p_talent_id:    talentId || null,
        p_source:       valueProp ? 'become_fan_popup' : 'bio_modal',
        p_referrer:     document.referrer || null,
        p_utm_source:   params.get('utm_source'),
        p_utm_medium:   params.get('utm_medium'),
        p_utm_campaign: params.get('utm_campaign'),
      });
    } catch { /* never block auth on lead-capture */ }

    try {
      const { data } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: cleanEmail, checkEmailOnly: true },
      });
      if (data?.sentToExistingPhone) {
        // Server returns the unmasked E.164 phone so we can echo it back on verify.
        setPhone(data.phone || '');
        setIsExistingUserFlow(true);
        setStep('otp');
        toast.success(`Code sent to ${data.phone ? '***' + String(data.phone).slice(-4) : 'your phone'}`);
      } else {
        setStep('phone');
      }
    } catch (err) {
      toast.error('Could not check account. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitPhone = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: digits },
      });
      setStep('otp');
      toast.success('Verification code sent');
    } catch {
      toast.error('Could not send code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async () => {
    if (otp.length < 4) return;
    setLoading(true);
    try {
      // Always echo the phone back so verify-registration-otp can find the OTP row
      // by phone (matches how AccountPage.tsx works). Strip non-digits; the server
      // formats it to E.164.
      const verifyPhone = phone && !phone.includes('*') ? phone.replace(/\D/g, '') : undefined;
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: verifyPhone, code: otp.trim() },
      });

      // supabase-js returns FunctionsHttpError for 4xx responses with data=null.
      // The real message lives on error.context (a Response). Parse it so we
      // surface "Invalid code"/"Code expired"/etc. instead of a generic fallback.
      let payload: any = data;
      if (!payload && error && (error as any).context) {
        try {
          const ctx = (error as any).context;
          if (typeof ctx.json === 'function') payload = await ctx.clone().json();
          else if (typeof ctx.text === 'function') {
            const t = await ctx.clone().text();
            try { payload = JSON.parse(t); } catch { payload = { error: t }; }
          }
        } catch {}
      }

      if (!payload?.success) {
        toast.error(payload?.error || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }

      if (payload.session?.access_token && payload.session?.refresh_token) {
        try {
          await supabase.auth.setSession({
            access_token: payload.session.access_token,
            refresh_token: payload.session.refresh_token,
          });
        } catch {}
      }

      localStorage.setItem('bio_subscriber_email', email.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');

      let userRow: any = null;
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data: u } = await supabase
          .from('users')
          .select('id, email, full_name, avatar_url, credits')
          .eq('id', session.user.id)
          .maybeSingle();
        userRow = u;
      }
      if (!userRow) {
        const { data: u } = await supabase
          .from('users')
          .select('id, email, full_name, avatar_url, credits')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle();
        userRow = u;
      }

      if (userRow?.id) {
        await linkFanToTalent(userRow.id);
        setResolvedUser(userRow);
        onAuthenticated?.(userRow);
        toast.success(talentFirstName ? `You're now a fan of ${talentFirstName}!` : 'Signed in');
        setStep('done');
        setTimeout(() => onClose(), 1400);
      } else {
        toast.success('Signed in');
        onClose();
      }
    } catch {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // ── Theme derivation ─────────────────────────────────────────────────────
  // The popup picks up the talent's accent color (button_color from
  // bio_settings) and uses it for the CTA, perks checkmarks, the avatar
  // ring, and the soft top-of-modal aurora glow. Falls back to the
  // generic ShoutOut blue/green palette when no theme is supplied.
  const accent = theme?.color || '#3b82f6';
  const accentRgb = parseColor(accent);
  // Pick a contrasting text color for the CTA so we don't end up with
  // white-on-yellow or black-on-navy. Same luminance test the bio bar
  // uses elsewhere.
  const ctaTextColor =
    (0.299 * accentRgb.r + 0.587 * accentRgb.g + 0.114 * accentRgb.b) / 255 > 0.6
      ? '#0b1020'
      : '#ffffff';
  const accentSoft = rgbaWithAlpha(accentRgb, 0.18);
  const accentMid = rgbaWithAlpha(accentRgb, 0.45);
  const accentGlow = rgbaWithAlpha(accentRgb, 0.55);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      {/* Scoped keyframes for the avatar ring shimmer + success burst */}
      <style>{`
        @keyframes acctRingPulse {
          0%, 100% { transform: scale(1);    opacity: 0.55; }
          50%      { transform: scale(1.06); opacity: 0.95; }
        }
        @keyframes acctRingSpin {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        @keyframes acctSuccessBurst {
          0%   { transform: scale(0.4); opacity: 0;   }
          40%  { transform: scale(1.05); opacity: 1;  }
          100% { transform: scale(1);    opacity: 1;  }
        }
        @keyframes acctSuccessWave {
          0%   { transform: scale(1);   opacity: 0.7; }
          100% { transform: scale(2.6); opacity: 0;   }
        }
      `}</style>

      <div
        className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: `radial-gradient(140% 100% at 50% -20%, ${accentSoft} 0%, transparent 55%), linear-gradient(180deg, #0b1020 0%, #0f172a 60%, #0a0f1d 100%)`,
          border: `1px solid ${accentMid}`,
          boxShadow: `0 30px 80px -20px ${accentGlow}, 0 0 0 1px ${accentSoft}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Subtle aurora streaks behind everything */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(60% 40% at 0% 0%, ${accentSoft} 0%, transparent 70%), radial-gradient(60% 40% at 100% 0%, ${accentSoft} 0%, transparent 70%)`,
            opacity: 0.9,
          }}
        />
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 z-10"
          aria-label="Close"
        >
          ×
        </button>

        <div className="relative px-6 pt-7 pb-6">
          {/* Hero header: talent avatar in a glowing/animated ring (when
              theme is supplied), otherwise the original ShoutOut wordmark. */}
          <div className="text-center mb-4">
            {theme?.avatarUrl ? (
              <div
                className="relative w-20 h-20 mx-auto mb-3"
                style={{ filter: `drop-shadow(0 8px 24px ${accentGlow})` }}
              >
                {/* outer rotating gradient ring */}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(from 0deg, ${accent}, ${rgbaWithAlpha(accentRgb, 0.15)}, ${accent})`,
                    animation: 'acctRingSpin 6s linear infinite',
                  }}
                />
                {/* soft pulsing halo */}
                <span
                  aria-hidden="true"
                  className="absolute inset-[-6px] rounded-full"
                  style={{
                    background: `radial-gradient(closest-side, ${accentMid} 0%, transparent 70%)`,
                    animation: 'acctRingPulse 2.4s ease-in-out infinite',
                  }}
                />
                {/* avatar disc */}
                <span
                  className="absolute inset-[3px] rounded-full overflow-hidden border-2"
                  style={{ borderColor: 'rgba(0,0,0,0.45)' }}
                >
                  <img
                    src={theme.avatarUrl}
                    alt={talentFirstName || 'Creator'}
                    className="w-full h-full object-cover"
                  />
                </span>
              </div>
            ) : (
              <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-5 opacity-70 mx-auto mb-3" />
            )}
            {step === 'done' ? (
              <>
                <h2 className="text-xl font-bold text-white">You're in</h2>
                {talentFirstName && (
                  <p className="text-sm text-gray-300 mt-1">
                    Linked as a fan of <span className="font-semibold" style={{ color: accent }}>{talentFirstName}</span>
                  </p>
                )}
              </>
            ) : valueProp && step === 'email' ? (
              <>
                <h2 className="text-xl font-extrabold text-white leading-tight tracking-tight">
                  {valueProp.title}
                </h2>
                {valueProp.subtitle && (
                  <p className="text-sm text-gray-200 mt-2 leading-snug">{valueProp.subtitle}</p>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-white">
                  {talentFirstName ? `Sign in to support ${talentFirstName}` : 'Sign in or create an account'}
                </h2>
                <p className="text-xs text-gray-300 mt-1">
                  {step === 'email' && "We'll send a one-time code to verify your account."}
                  {step === 'phone' && "We'll text you a code. Standard rates apply."}
                  {step === 'otp' && (isExistingUserFlow && phone
                    ? `Enter the 6-digit code we sent to ***${String(phone).slice(-4)}`
                    : 'Enter the 6-digit code we sent to your phone')}
                </p>
              </>
            )}
          </div>

          {step === 'email' && (
            <div className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitEmail()}
                placeholder="you@email.com"
                autoFocus
                className="w-full bg-white/[0.06] border px-4 py-3 rounded-xl text-white placeholder-gray-400 focus:outline-none transition-colors"
                style={{ borderColor: accentSoft }}
                onFocus={(e) => (e.currentTarget.style.borderColor = accentMid)}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = accentSoft;
                  // Auto-correct common typos (gmail.co → gmail.com) the
                  // moment the user tabs out, before they ever click submit.
                  if (email) {
                    const corrected = correctEmailTypo(email);
                    if (corrected && corrected !== email.toLowerCase().trim()) {
                      setEmail(corrected);
                      toast.success(`Auto-corrected to ${corrected}`);
                    }
                  }
                }}
              />
              <button
                onClick={submitEmail}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-transform active:scale-[0.98]"
                style={{
                  backgroundColor: accent,
                  color: ctaTextColor,
                  boxShadow: `0 8px 24px -6px ${accentGlow}, 0 0 0 1px ${accentMid}`,
                }}
              >
                {loading ? 'Checking…' : valueProp ? 'Become a Fan' : 'Continue'}
              </button>
              {valueProp?.perks && valueProp.perks.length > 0 && (
                <ul className="pt-3 space-y-2">
                  {valueProp.perks.map((perk, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-xs text-gray-200 leading-snug">
                      <span
                        className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: accentSoft, boxShadow: `inset 0 0 0 1px ${accentMid}` }}
                        aria-hidden="true"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill={accent}
                          className="h-3 w-3"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.296a1 1 0 010 1.408l-7.5 7.5a1 1 0 01-1.408 0l-3.5-3.5a1 1 0 011.408-1.408L8.5 12.092l6.796-6.796a1 1 0 011.408 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* Review carousel — minimal text + stars only, one
                  review at a time, with dot indicators below.
                  Auto-advances every 5s so a fan sees real social
                  proof from previous customers without having to
                  scroll. */}
              {valueProp && reviews.length > 0 && (
                <div className="pt-5">
                  {/* Hide WebKit scrollbar on the carousel — keep
                      native drag/swipe but no scrollbar chrome. */}
                  <style>{`.acct-review-row::-webkit-scrollbar{display:none;}`}</style>
                  <div
                    ref={reviewScrollRef}
                    className="acct-review-row flex overflow-x-auto scroll-smooth"
                    style={{
                      scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                    }}
                  >
                    {reviews.map((r) => (
                      <div
                        key={r.id}
                        className="flex-shrink-0 w-full px-1 text-center"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <div className="flex items-center justify-center gap-1 mb-1.5">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <svg
                              key={i}
                              viewBox="0 0 20 20"
                              fill={i < r.rating ? accent : rgbaWithAlpha(accentRgb, 0.25)}
                              className="w-3 h-3"
                              aria-hidden="true"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.287 3.957c.299.92-.755 1.688-1.539 1.118L10 15.347l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.957a1 1 0 00-.364-1.118L2.65 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-xs text-gray-100 leading-snug line-clamp-3 italic">
                          “{(r.comment || '').trim()}”
                        </p>
                        {r.full_name && (
                          <p className="text-[11px] text-gray-400 mt-1.5">
                            — {r.full_name.split(' ')[0]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Dot indicators — only show when there's more than
                      one review, otherwise the single dot is noise. */}
                  {reviews.length > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-3">
                      {reviews.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setReviewIndex(i)}
                          aria-label={`Show review ${i + 1}`}
                          className="rounded-full transition-all duration-300"
                          style={{
                            width: i === reviewIndex ? 16 : 5,
                            height: 5,
                            backgroundColor: i === reviewIndex ? accent : rgbaWithAlpha(accentRgb, 0.3),
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'phone' && (
            <div className="space-y-3">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && submitPhone()}
                placeholder="(555) 555-1234"
                autoFocus
                inputMode="numeric"
                className="w-full bg-white/[0.06] border px-4 py-3 rounded-xl text-white placeholder-gray-400 focus:outline-none transition-colors"
                style={{ borderColor: accentSoft }}
                onFocus={(e) => (e.currentTarget.style.borderColor = accentMid)}
                onBlur={(e) => (e.currentTarget.style.borderColor = accentSoft)}
              />
              <button
                onClick={submitPhone}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-transform active:scale-[0.98]"
                style={{
                  backgroundColor: accent,
                  color: ctaTextColor,
                  boxShadow: `0 8px 24px -6px ${accentGlow}, 0 0 0 1px ${accentMid}`,
                }}
              >
                {loading ? 'Sending…' : 'Send code'}
              </button>
              <button onClick={() => setStep('email')} className="w-full text-gray-400 text-xs hover:text-white">
                ← change email
              </button>
            </div>
          )}

          {step === 'otp' && (
            <div className="space-y-3">
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => e.key === 'Enter' && submitOtp()}
                placeholder="123456"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                className="w-full bg-white/[0.06] border px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none text-center text-2xl tracking-[0.4em] transition-colors"
                style={{ borderColor: accentSoft }}
                onFocus={(e) => (e.currentTarget.style.borderColor = accentMid)}
                onBlur={(e) => (e.currentTarget.style.borderColor = accentSoft)}
              />
              <button
                onClick={submitOtp}
                disabled={loading || otp.length < 4}
                className="w-full py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-transform active:scale-[0.98]"
                style={{
                  backgroundColor: accent,
                  color: ctaTextColor,
                  boxShadow: `0 8px 24px -6px ${accentGlow}, 0 0 0 1px ${accentMid}`,
                }}
              >
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <div className="flex justify-between text-xs">
                <button onClick={() => { setStep('email'); setOtp(''); setIsExistingUserFlow(false); setPhone(''); }} className="text-gray-400 hover:text-white">
                  ← change email
                </button>
                {!isExistingUserFlow && (
                  <button onClick={() => setStep('phone')} className="text-gray-400 hover:text-white">
                    change phone
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 'done' && resolvedUser && (
            <div className="space-y-3 text-center">
              {/* Themed success burst — pulsing rings + filled check */}
              <div className="relative w-16 h-16 mx-auto" aria-hidden="true">
                {[0, 0.18].map((delay, i) => (
                  <span
                    key={i}
                    className="absolute inset-0 rounded-full"
                    style={{
                      border: `2px solid ${accent}`,
                      boxShadow: `0 0 18px ${accentGlow}`,
                      animation: 'acctSuccessWave 1.4s ease-out forwards',
                      animationDelay: `${delay}s`,
                    }}
                  />
                ))}
                <span
                  className="absolute inset-0 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: accentSoft,
                    boxShadow: `inset 0 0 0 1px ${accentMid}`,
                    animation: 'acctSuccessBurst 0.45s ease-out forwards',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill={accent}
                    className="w-7 h-7"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.296a1 1 0 010 1.408l-7.5 7.5a1 1 0 01-1.408 0l-3.5-3.5a1 1 0 011.408-1.408L8.5 12.092l6.796-6.796a1 1 0 011.408 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>
              <p className="text-xs text-gray-300 leading-snug px-2">
                Support, order, or check in daily to rank as a Top Fan.
              </p>
              {mode === 'full' && (
                <a href="/account" className="block text-xs hover:opacity-80" style={{ color: accent }}>
                  Manage your account →
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountModal;
